import { useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { alpha } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import InventoryIcon from '@mui/icons-material/Inventory';
import SpeedIcon from '@mui/icons-material/Speed';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import axiosInstance from '../../lib/axios';
import { getUser } from '../../utils/auth';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { BlurredFeatureOverlay } from '../../components/BlurredFeatureOverlay';
import { AnalyticsHeader } from '../../components/analytics/AnalyticsHeader';
import {
  RestockRecommendationsResponse,
  ProductDemandData,
  VelocityClassification,
  RestockPriority,
  VelocityClassificationLabels,
  RestockPriorityLabels,
} from '../../types/analytics';

interface DemandParams {
  organizationId?: string;
  locationId?: string;
  velocityType?: VelocityClassification;
  startDate?: string;
  endDate?: string;
  forecastDays?: number;
  limit?: number;
}

const fetchDemandPlanning = async (params: DemandParams): Promise<RestockRecommendationsResponse> => {
  const user = getUser();
  const queryParams: Record<string, any> = {
    forecastDays: params.forecastDays || 30,
  };

  if (params.startDate) {
    queryParams.startDate = params.startDate;
  }
  if (params.endDate) {
    queryParams.endDate = params.endDate;
  }
  if (user?.locationId) {
    queryParams.locationId = user.locationId;
  }
  if (params.velocityType) {
    queryParams.velocityType = params.velocityType;
  }
  if (params.limit) {
    queryParams.limit = params.limit;
  }

  const response = await axiosInstance.get('/analytics/demand', { params: queryParams });
  return response.data;
};

const getVelocityChipColor = (velocity: VelocityClassification): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  switch (velocity) {
    case VelocityClassification.FAST:
      return 'success';
    case VelocityClassification.MEDIUM:
      return 'info';
    case VelocityClassification.SLOW:
      return 'warning';
    case VelocityClassification.STAGNANT:
      return 'error';
    default:
      return 'default';
  }
};

const getPriorityChipColor = (priority: RestockPriority): 'error' | 'warning' | 'info' | 'success' | 'default' => {
  switch (priority) {
    case RestockPriority.HIGH:
      return 'error';
    case RestockPriority.MEDIUM:
      return 'warning';
    case RestockPriority.LOW:
      return 'info';
    case RestockPriority.NONE:
      return 'success';
    default:
      return 'default';
  }
};

const getStockoutUrgency = (days: number | null): { color: 'error' | 'warning' | 'success' | 'default'; label: string } => {
  if (days === null) {
    return { color: 'default', label: 'N/A' };
  }
  if (days <= 7) {
    return { color: 'error', label: `${days} days` };
  }
  if (days <= 14) {
    return { color: 'warning', label: `${days} days` };
  }
  return { color: 'success', label: `${days} days` };
};

export const DemandPlanning = () => {
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const { hasAccess, isLoading: moduleLoading } = useModuleAccess('advanced_analytics');

  // Date range state - default to last 30 days
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [selectedPreset, setSelectedPreset] = useState<string>('30d');
  const [forecastDays, setForecastDays] = useState<number>(30);
  const [velocityFilter, setVelocityFilter] = useState<VelocityClassification | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<RestockPriority | ''>('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const {
    data: demandData,
    isLoading,
    error,
    dataUpdatedAt,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['demandPlanning', startDate, endDate, forecastDays, velocityFilter],
    queryFn: () =>
      fetchDemandPlanning({
        forecastDays,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        velocityType: velocityFilter || undefined,
      }),
    enabled: hasAccess && !moduleLoading,
    staleTime: 5 * 60 * 1000,       // Data fresh for 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  const handlePresetClick = (preset: string) => {
    const end = new Date();
    const start = new Date();

    switch (preset) {
      case '7d':
        start.setDate(start.getDate() - 7);
        setForecastDays(14);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        setForecastDays(30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        setForecastDays(60);
        break;
      case '1y':
        start.setFullYear(start.getFullYear() - 1);
        setForecastDays(90);
        break;
    }

    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(preset);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          borderLight: palette.borderLight,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          borderLight: palette.light.borderLight,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  const cardStyles = {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: theme.radius.xl,
    p: 3,
    transition: theme.transitions.normal,
  };

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getDaysRange = () => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Handle module access loading
  if (moduleLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  // Prepare velocity distribution chart data
  const velocityChartData = demandData?.velocityDistribution
    ? [
        { name: 'Fast Moving', value: demandData.velocityDistribution.fast, color: palette.success },
        { name: 'Medium Moving', value: demandData.velocityDistribution.medium, color: palette.info },
        { name: 'Slow Moving', value: demandData.velocityDistribution.slow, color: palette.warning },
        { name: 'Stagnant', value: demandData.velocityDistribution.stagnant, color: palette.error },
      ].filter((item) => item.value > 0)
    : [];

  // Prepare priority breakdown chart data
  const priorityChartData = demandData?.summary
    ? [
        { name: 'High Priority', value: demandData.summary.highPriority, color: palette.error },
        { name: 'Medium Priority', value: demandData.summary.mediumPriority, color: palette.warning },
        { name: 'Low Priority', value: demandData.summary.lowPriority, color: palette.info },
      ].filter((item) => item.value > 0)
    : [];

  // Filter recommendations based on priority filter
  const filteredRecommendations =
    demandData?.recommendations?.filter((item) => {
      if (priorityFilter && item.restockPriority !== priorityFilter) {
        return false;
      }
      return true;
    }) || [];

  // DataGrid columns
  const columns: GridColDef<ProductDemandData>[] = [
    {
      field: 'productName',
      headerName: 'Product',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'sku',
      headerName: 'SKU',
      width: 120,
    },
    {
      field: 'currentStock',
      headerName: 'Current Stock',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value <= 0 ? 'error' : params.value < 10 ? 'warning' : 'success'}
          size="small"
        />
      ),
    },
    {
      field: 'velocityClassification',
      headerName: 'Velocity',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={VelocityClassificationLabels[params.value as VelocityClassification]}
          color={getVelocityChipColor(params.value as VelocityClassification)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'averageDailyDemand',
      headerName: 'Avg Daily Demand',
      width: 150,
      valueFormatter: (params: any) => params.value?.toFixed(2) || '0',
    },
    {
      field: 'forecastedDemand',
      headerName: 'Forecasted Demand',
      width: 160,
      valueFormatter: (params: any) => params.value?.toFixed(0) || '0',
    },
    {
      field: 'daysUntilStockout',
      headerName: 'Days to Stockout',
      width: 150,
      renderCell: (params) => {
        const { color, label } = getStockoutUrgency(params.value);
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {params.value !== null && params.value <= 7 && (
              <WarningIcon color="error" fontSize="small" />
            )}
            <Chip label={label} color={color} size="small" />
          </Stack>
        );
      },
    },
    {
      field: 'restockPriority',
      headerName: 'Priority',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={RestockPriorityLabels[params.value as RestockPriority]}
          color={getPriorityChipColor(params.value as RestockPriority)}
          size="small"
        />
      ),
    },
    {
      field: 'recommendedOrderQuantity',
      headerName: 'Order Qty',
      width: 120,
      renderCell: (params) => (
        <Typography
          fontWeight={params.value > 0 ? 'bold' : 'normal'}
          color={params.value > 0 ? 'primary' : 'text.secondary'}
        >
          {params.value}
        </Typography>
      ),
    },
  ];

  const pageContent = (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Page Header */}
      <AnalyticsHeader
        category="Inventory Analytics"
        title="Demand Planning"
        subtitle={`Inventory velocity classification and restock recommendations - ${getDaysRange()} days of historical data`}
        dataUpdatedAt={dataUpdatedAt}
        isRefetching={isRefetching}
        onRefresh={() => refetch()}
      />

      {/* Date Range Picker */}
      <Box
        sx={{
          ...cardStyles,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
            }}
          >
            Time Period
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap' }}>
          <ButtonGroup
            variant="outlined"
            sx={{
              '& .MuiButton-root': {
                borderColor: colors.border,
                color: colors.textSecondary,
                textTransform: 'none',
                fontWeight: typography.fontWeight.medium,
                fontSize: typography.fontSize.sm,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: palette.primaryMuted,
                },
              },
            }}
          >
            {['7d', '30d', '90d', '1y'].map((preset) => (
              <Button
                key={preset}
                onClick={() => handlePresetClick(preset)}
                sx={{
                  ...(selectedPreset === preset && {
                    backgroundColor: palette.primaryMuted,
                    borderColor: `${palette.primary} !important`,
                    color: palette.primary,
                  }),
                }}
              >
                {preset === '7d' && 'Last 7 days'}
                {preset === '30d' && 'Last 30 days'}
                {preset === '90d' && 'Last 90 days'}
                {preset === '1y' && 'Last year'}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              type="date"
              label="Start Date"
              value={formatDateForInput(startDate)}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              to
            </Typography>
            <TextField
              type="date"
              label="End Date"
              value={formatDateForInput(endDate)}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: palette.primary }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box p={3}>
          <Alert
            severity="error"
            sx={{
              borderRadius: theme.radius.lg,
              backgroundColor: palette.errorMuted,
              color: palette.error,
            }}
          >
            {error instanceof Error ? error.message : 'Failed to load demand planning data'}
          </Alert>
        </Box>
      )}

      {/* Data Loaded */}
      {!isLoading && !error && demandData && (
        <>
          {/* Priority Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.primary,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Total Products
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: colors.textPrimary,
                        mt: 1,
                      }}
                    >
                      {demandData.summary.totalProducts}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      analyzed in period
                    </Typography>
                  </Box>
                  <InventoryIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.error,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      High Priority
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: palette.error,
                        mt: 1,
                      }}
                    >
                      {demandData.summary.highPriority}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      need immediate restock
                    </Typography>
                  </Box>
                  <PriorityHighIcon sx={{ fontSize: 40, color: alpha(palette.error, 0.2) }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.warning,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Medium Priority
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: palette.warning,
                        mt: 1,
                      }}
                    >
                      {demandData.summary.mediumPriority}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      restock soon
                    </Typography>
                  </Box>
                  <WarningIcon sx={{ fontSize: 40, color: alpha(palette.warning, 0.2) }} />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  ...cardStyles,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: palette.success,
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                      }}
                    >
                      Low Priority
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: palette.success,
                        mt: 1,
                      }}
                    >
                      {demandData.summary.lowPriority}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      stock adequate
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Velocity Distribution Chart */}
            <Grid item xs={12} md={6}>
              <Box sx={cardStyles}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <SpeedIcon sx={{ color: palette.primary }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.textPrimary,
                    }}
                  >
                    Velocity Distribution
                  </Typography>
                </Box>
                {velocityChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={velocityChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {velocityChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: colors.surface,
                          border: `1px solid ${colors.border}`,
                          borderRadius: theme.radius.md,
                          color: colors.textPrimary,
                        }}
                      />
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: colors.textPrimary }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 300,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      No velocity data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Priority Breakdown Chart */}
            <Grid item xs={12} md={6}>
              <Box sx={cardStyles}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <LocalShippingIcon sx={{ color: palette.primary }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.textPrimary,
                    }}
                  >
                    Restock Priority Breakdown
                  </Typography>
                </Box>
                {priorityChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={priorityChartData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                      <XAxis type="number" stroke={colors.textMuted} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        stroke={colors.textMuted}
                        width={120}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: colors.surface,
                          border: `1px solid ${colors.border}`,
                          borderRadius: theme.radius.md,
                          color: colors.textPrimary,
                        }}
                      />
                      <Bar dataKey="value" name="Products">
                        {priorityChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 300,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      No priority data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Recommendations DataGrid */}
          <Box sx={cardStyles}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon sx={{ color: palette.primary }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                  }}
                >
                  Restock Recommendations
                </Typography>
              </Box>

              {/* Filters */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel
                    sx={{
                      color: colors.textMuted,
                      '&.Mui-focused': { color: palette.primary },
                    }}
                  >
                    Velocity
                  </InputLabel>
                  <Select
                    value={velocityFilter}
                    label="Velocity"
                    onChange={(e) => setVelocityFilter(e.target.value as VelocityClassification | '')}
                    sx={{
                      backgroundColor: colors.surfaceElevated,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderLight },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary },
                      '& .MuiSelect-select': { color: colors.textPrimary },
                    }}
                  >
                    <MenuItem value="">All Velocities</MenuItem>
                    {Object.values(VelocityClassification).map((velocity) => (
                      <MenuItem key={velocity} value={velocity}>
                        {VelocityClassificationLabels[velocity]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel
                    sx={{
                      color: colors.textMuted,
                      '&.Mui-focused': { color: palette.primary },
                    }}
                  >
                    Priority
                  </InputLabel>
                  <Select
                    value={priorityFilter}
                    label="Priority"
                    onChange={(e) => setPriorityFilter(e.target.value as RestockPriority | '')}
                    sx={{
                      backgroundColor: colors.surfaceElevated,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderLight },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary },
                      '& .MuiSelect-select': { color: colors.textPrimary },
                    }}
                  >
                    <MenuItem value="">All Priorities</MenuItem>
                    {Object.values(RestockPriority).map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {RestockPriorityLabels[priority]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            {filteredRecommendations.length > 0 ? (
              <DataGrid
                rows={filteredRecommendations}
                columns={columns}
                rowCount={filteredRecommendations.length}
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                autoHeight
                disableRowSelectionOnClick
                getRowId={(row) => row.productId}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    borderColor: colors.border,
                    color: colors.textPrimary,
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.surfaceElevated,
                    borderColor: colors.border,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderColor: colors.border,
                  },
                  '& .MuiTablePagination-root': {
                    color: colors.textSecondary,
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                }}
              >
                <InventoryIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
                <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
                  No Recommendations
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary, textAlign: 'center', maxWidth: 400 }}
                >
                  {priorityFilter || velocityFilter
                    ? 'No products match the selected filters. Try adjusting your filter criteria.'
                    : 'No restock recommendations available for this period.'}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Empty State */}
      {!isLoading && !error && !demandData && (
        <Box sx={cardStyles}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <SpeedIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
              No Demand Planning Data Available
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary, textAlign: 'center', maxWidth: 400 }}
            >
              There isn&apos;t enough historical sales data to generate demand planning insights.
              Continue making sales and check back later.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  // Wrap content with BlurredFeatureOverlay for non-PROFESSIONAL users
  return (
    <BlurredFeatureOverlay feature="Demand Planning" requiredTier="professional">
      {pageContent}
    </BlurredFeatureOverlay>
  );
};

export default DemandPlanning;
